import type {
  LeadFlowAnswerFieldName,
  LeadFlowAnswers,
  LeadFlowDocument,
} from "../types";

import {
  getLeadFlowStepEntryMissingFieldNames,
  LEAD_FORM_DELIVERY_CONTEXT,
} from "@homeinstead/forms/lead-form";
import { CONTRACT_SHAPE } from "../constants/contract-shape";

type ResolveLeadFlowStepIndexInput = {
  answers: LeadFlowAnswers;
  leadFlow: Pick<LeadFlowDocument, "deliveryContext" | "formType" | "steps">;
  stepParameter: string | null;
};

/**
 * Fields the visitor has already given a valid answer for, from this session,
 * an earlier reload, or an allowed query prefill. A national postal code
 * counts only once it has resolved to an office.
 */
function getAnsweredFieldNames(
  answers: LeadFlowAnswers,
  deliveryContext: LeadFlowDocument["deliveryContext"],
): Set<string> {
  const answeredFieldNames = new Set(
    Object.entries(answers)
      .filter(
        ([fieldName, value]) =>
          value !== undefined &&
          CONTRACT_SHAPE[fieldName as LeadFlowAnswerFieldName]?.safeParse(value)
            .success,
      )
      .map(([fieldName]) => fieldName),
  );

  if (
    deliveryContext === LEAD_FORM_DELIVERY_CONTEXT.national &&
    answers.organizationNumber === undefined
  ) {
    answeredFieldNames.delete("postalCode");
  }

  return answeredFieldNames;
}

/**
 * The step a lead flow shows for its URL step parameter. A visitor may land on
 * a later step only when every contract-required field asked before it is
 * answered, defaulted, or prefilled. Otherwise, and for an unknown step
 * id, the flow starts at the first step and `shouldResetParameter` asks the
 * caller to drop the parameter from the URL.
 */
function resolveStepIndex({
  answers,
  leadFlow,
  stepParameter,
}: ResolveLeadFlowStepIndexInput): {
  shouldResetParameter: boolean;
  stepIndex: number;
} {
  const steps = leadFlow.steps ?? [];
  if (stepParameter === null) {
    return { shouldResetParameter: false, stepIndex: 0 };
  }

  const requestedIndex = steps.findIndex((step) => step.id === stepParameter);
  if (requestedIndex <= 0) {
    return { shouldResetParameter: requestedIndex < 0, stepIndex: 0 };
  }

  const answeredFieldNames = getAnsweredFieldNames(
    answers,
    leadFlow.deliveryContext,
  );
  const missingFieldNames = getLeadFlowStepEntryMissingFieldNames({
    deliveryContext: leadFlow.deliveryContext,
    formType: leadFlow.formType,
    stepIndex: requestedIndex,
    steps: steps.map((step) => ({
      items: step._type === "leadFlowStep" ? step.items : [],
    })),
  }).filter((fieldName) => !answeredFieldNames.has(fieldName));

  return missingFieldNames.length === 0
    ? { shouldResetParameter: false, stepIndex: requestedIndex }
    : { shouldResetParameter: true, stepIndex: 0 };
}

export { resolveStepIndex };
